import { useNavigate } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { ChatTabIcon } from '@/components/sidebar/icons/SidebarIcons';
import { EMPTY_VALUE } from '../taskTheme';
import type { TaskSource } from '@/types/task';

interface TaskSourceRowProps {
  source: TaskSource | null;
}

/**
 * Where the task came from: the conversation, by name, as a link to it.
 *
 * Always a link, including inside that same conversation — the name is what
 * tells the reader which chat it was, and a panel reached from the navigator or
 * a card can be open over a different thread than the one it names. Unfilled at
 * rest like every other property value; the mark sits a step back and firms to
 * ink on hover, and the arrow only appears then (or on keyboard focus), over the same warm wash the other rows hover with — on
 * the same 30px box and gutter bleed, so its icon lines up with theirs. The
 * glyph is the sidebar's Chat tab icon, so "a chat" is one mark everywhere.
 */
export const TaskSourceRow = ({ source }: TaskSourceRowProps) => {
  const navigate = useNavigate();

  if (!source) {
    return <span className={EMPTY_VALUE}>—</span>;
  }

  return (
    <button
      type="button"
      className="group -mx-[9px] inline-flex h-[30px] min-w-0 max-w-[calc(100%_+_18px)] items-center gap-[7px] rounded-md px-[9px] text-[13.5px] text-[#181818] transition-colors hover:bg-[#F6F5F1] focus-visible:bg-[#F6F5F1]"
      onClick={() => navigate(`/ui/chat/${source.workflow_execution_id}`)}
    >
      <ChatTabIcon className="size-4 shrink-0 text-[#9A9A9A] transition-colors group-hover:text-[#181818]" />
      <span className="truncate">{source.title || 'Chat'}</span>
      <ArrowUpRight
        className="size-3.5 shrink-0 text-[#181818] opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
        aria-hidden
      />
    </button>
  );
};
